import { useCallback, useEffect, useState } from 'react';
import { useBusiness } from '../contexts/BusinessContext';
import {
  fetchInventoryConsumedByCategory,
  type InventoryCategoryGroup,
} from '../lib/inventoryConsumedReport';

/** Consumed stock grouped by category for the control-centre inventory widget. */
export function useInventoryConsumedReport(enabled: boolean) {
  const { selectedBusiness } = useBusiness();
  const businessId = selectedBusiness?.id ?? '';

  const [categories, setCategories] = useState<InventoryCategoryGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!enabled || !businessId) {
      setCategories([]);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const data = await fetchInventoryConsumedByCategory(businessId);
      setCategories(data);
    } catch (err) {
      console.error('Error loading consumed inventory report:', err);
      setError(err instanceof Error ? err.message : 'Failed to load consumed report');
      setCategories([]);
    } finally {
      setLoading(false);
    }
  }, [businessId, enabled]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const totalAmount = categories.reduce((sum, cat) => sum + cat.totalAmount, 0);
  const totalUnits = categories.reduce((sum, cat) => sum + cat.totalUnits, 0);

  return {
    categories,
    totalAmount,
    totalUnits,
    loading,
    error,
    refresh,
  };
}
